import productStyles from './ProductLogo.module.css';
import styles from './ClicksMondayGisLogo.module.css';

const BRAND_LABEL = 'Clicks Monday GIS';

interface MarkProps {
  size?: number;
  className?: string;
}

/**
 * GIS map mark — same illustration language as Tourism 360 globe:
 * map extent, grid, parcel polygon, location pin, amber spark.
 */
export function ClicksMondayGisMark({ size, className = '' }: MarkProps) {
  const dim = size != null ? { width: size, height: size } : {};
  return (
    <svg
      {...dim}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className={className}
    >
      <circle cx="50" cy="50" r="40" fill="var(--color-accent-soft)" />
      <circle
        cx="50"
        cy="50"
        r="40"
        stroke="var(--color-brand-deep)"
        strokeWidth="3.5"
        fill="none"
      />

      {/* Map grid */}
      <line
        x1="14"
        y1="50"
        x2="86"
        y2="50"
        stroke="var(--color-brand-line)"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.9"
      />
      <line
        x1="50"
        y1="14"
        x2="50"
        y2="86"
        stroke="var(--color-brand-line)"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.9"
      />
      <line
        x1="14"
        y1="34"
        x2="86"
        y2="34"
        stroke="var(--color-brand-pale)"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.75"
      />
      <line
        x1="14"
        y1="66"
        x2="86"
        y2="66"
        stroke="var(--color-brand-pale)"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.75"
      />
      <line
        x1="34"
        y1="14"
        x2="34"
        y2="86"
        stroke="var(--color-brand-pale)"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.75"
      />
      <line
        x1="66"
        y1="14"
        x2="66"
        y2="86"
        stroke="var(--color-brand-pale)"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.75"
      />

      {/* Parcel polygon */}
      <path
        d="M32 58 L48 44 L68 52 L62 72 L38 70 Z"
        fill="var(--color-brand-node)"
        fillOpacity="0.35"
        stroke="var(--color-brand-node)"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />

      {/* Location pin */}
      <path
        d="M54 38 C60 38 64 44 64 50 C64 58 54 68 54 68 C54 68 44 58 44 50 C44 44 48 38 54 38Z"
        fill="var(--color-brand-deep)"
      />
      <circle cx="54" cy="48" r="4.5" fill="var(--color-brand-pale)" />

      {/* Amber spark — sync / live data (matches Tourism globe) */}
      <circle cx="68" cy="32" r="5" fill="var(--color-brand-spark)" />
      <path
        d="M68 37 L68 46"
        stroke="var(--color-brand-spark)"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface LogoProps {
  variant?: 'full' | 'compact';
  className?: string;
  decorative?: boolean;
}

/** Wordmark lockup — same typography rhythm as ProductLogo (Tourism 360). */
export function ClicksMondayGisLogo({
  variant = 'full',
  className = '',
  decorative = false,
}: LogoProps) {
  const a11y = decorative
    ? ({ 'aria-hidden': true as const })
    : ({ 'aria-label': BRAND_LABEL });

  const lockupClass =
    `brand-lockup ${productStyles.lockup} ${
      variant === 'compact' ? productStyles.compactLockup : ''
    } ${className}`.trim();

  return (
    <div className={lockupClass} dir="ltr" {...a11y}>
      <ClicksMondayGisMark className={productStyles.mark} />
      <span className={`brand-clicks ${productStyles.clicks}`}>Clicks</span>
      <span className={`brand-name ${productStyles.name}`}>Monday</span>
      <span className={`brand-badge ${productStyles.badge}`}>GIS</span>
    </div>
  );
}

interface PoweredByProps {
  className?: string;
}

export function PoweredByClicksMondayGis({ className = '' }: PoweredByProps) {
  return (
    <div
      className={`${styles.poweredBy} ${className}`.trim()}
      dir="ltr"
      role="contentinfo"
      aria-label={`Powered by ${BRAND_LABEL}`}
    >
      <span className={styles.poweredLabel}>Powered by</span>
      <ClicksMondayGisLogo variant="compact" decorative />
    </div>
  );
}
